/**
 * The JSON attribute path grammar: a path through an attribute's JSON value,
 * flattened into the leaf's own `field` string.
 *
 * `orders?.sku` reads "orders, where at least one element has sku", and
 * `profile.plan` reads "profile, which has the property plan". The path builder
 * expands such a field into one level per step and writes the levels back as
 * this string, so a built path is always an ordinary attribute leaf whose
 * `field` happens to be a path.
 *
 * The four action tokens below are the builder's own vocabulary, never stored
 * operators: the backend declares none of them, so a leaf's `operator` is
 * always an ordinary terminal comparison.
 */

/** How many steps a path may take below its root attribute. */
export const MAX_JSON_DEPTH = 7;

export const JSON_PATH_ACTIONS = {
  /** `A[1].B`, an array index. Only the free-text path can hold it. */
  arrayIndexHasPropertyOrValue: 'array_index_has_property_or_value',
  /** `A?.B`, "where at least one": an array's elements, or an object's own property. */
  arrayOrObjectHasPropertyOrValue: 'array_or_object_has_property_or_value',
  /** `A[].B`, an array only. Only the free-text path can hold it. */
  arrayHasPropertyOrValue: 'array_has_property_or_value',
  /** `A.B`, "has the property". */
  objectHasProperty: 'object_has_property',
} as const;

export type JsonPathAction = (typeof JSON_PATH_ACTIONS)[keyof typeof JSON_PATH_ACTIONS];

/**
 * One level of a path. `operator` is the step from this level to the next, and
 * `''` on the last level.
 *
 * `arrayConditionType` says how the parent's array step treats this level:
 * matching a `'property'` of each element, or the element `'value'` itself. It
 * is set only on a level whose parent step is an array step.
 */
export type JsonPathItem = {
  name: string;
  operator: JsonPathAction | '';
  arrayConditionType?: 'property' | 'value';
  arrayIndex?: number;
};

const ARRAY_ACTIONS: readonly string[] = [
  JSON_PATH_ACTIONS.arrayHasPropertyOrValue,
  JSON_PATH_ACTIONS.arrayOrObjectHasPropertyOrValue,
  JSON_PATH_ACTIONS.arrayIndexHasPropertyOrValue,
];

/** Whether an operator pick is one of the two steps the level-by-level builder offers. */
export function isJsonPathAction(operator: string) {
  return (
    operator === JSON_PATH_ACTIONS.objectHasProperty || operator === JSON_PATH_ACTIONS.arrayOrObjectHasPropertyOrValue
  );
}

function isLastItemArrayStep(items: readonly JsonPathItem[]): boolean {
  const last = items.at(-1);
  return last != null && ARRAY_ACTIONS.includes(last.operator);
}

/** Where the tokenizer is in the string, and what it has read so far. */
type Tokenizer = {
  readonly str: string;
  index: number;
  items: JsonPathItem[];
  currentWord: string;
  isEscaping: boolean;
  isInSquareBrackets: boolean;
  /** Inside `["…"]`: the brackets opened on a quote, so this is a quoted key. */
  maybeBracketsWord: boolean;
  bracketsWord: string | null;
  arrayIndexString: string | null;
};

/**
 * Appends a level, marking it as an array element's property or value when
 * the step before it is an array step.
 */
function pushStep(tokenizer: Tokenizer, item: JsonPathItem) {
  if (isLastItemArrayStep(tokenizer.items)) {
    item.arrayConditionType = tokenizer.currentWord === '' ? 'value' : 'property';
  }
  tokenizer.items.push(item);
  tokenizer.currentWord = '';
}

function leaveSquareBrackets(tokenizer: Tokenizer) {
  tokenizer.isInSquareBrackets = false;
  tokenizer.arrayIndexString = null;
}

/**
 * Closes a quoted `["key"]`: the key becomes the current word, and the level
 * before it a property step, marked as an array element's property when it
 * follows an array step (`a?.b["key"]`).
 */
function closeQuotedKey(tokenizer: Tokenizer) {
  if (tokenizer.currentWord !== '') {
    pushStep(tokenizer, { name: tokenizer.currentWord, operator: JSON_PATH_ACTIONS.objectHasProperty });
  }
  tokenizer.currentWord = tokenizer.bracketsWord ?? '';
  tokenizer.bracketsWord = null;
  tokenizer.maybeBracketsWord = false;
  leaveSquareBrackets(tokenizer);
  tokenizer.index += 2;
}

/** Closes `[]` or `[n]`: the current word becomes an array step. */
function closeArrayStep(tokenizer: Tokenizer) {
  const item: JsonPathItem = { name: tokenizer.currentWord, operator: JSON_PATH_ACTIONS.arrayHasPropertyOrValue };
  if (tokenizer.arrayIndexString != null) {
    item.operator = JSON_PATH_ACTIONS.arrayIndexHasPropertyOrValue;
    item.arrayIndex = Number.parseInt(tokenizer.arrayIndexString);
  }
  leaveSquareBrackets(tokenizer);
  pushStep(tokenizer, item);
  tokenizer.index++;
}

/**
 * A character after an unquoted `[` that is not `]`. A digit extends the array
 * index; anything else means the brackets were part of the attribute's name,
 * so the character joins the word.
 */
function readBracketCharacter(tokenizer: Tokenizer, char: string) {
  if (Number.isNaN(Number.parseInt(char))) {
    leaveSquareBrackets(tokenizer);
    tokenizer.currentWord += char;
    tokenizer.isEscaping = false;
  } else {
    tokenizer.arrayIndexString = (tokenizer.arrayIndexString ?? '') + char;
  }
  tokenizer.index++;
}

function readInSquareBrackets(tokenizer: Tokenizer, char: string) {
  const { str, index } = tokenizer;
  if (char === '"' && str[index - 1] === '[') {
    tokenizer.maybeBracketsWord = true;
    tokenizer.index++;
  } else if (char === '"' && str[index + 1] === ']' && tokenizer.maybeBracketsWord) {
    closeQuotedKey(tokenizer);
  } else if (tokenizer.maybeBracketsWord) {
    tokenizer.bracketsWord = (tokenizer.bracketsWord ?? '') + char;
    tokenizer.index++;
  } else if (char === ']') {
    closeArrayStep(tokenizer);
  } else {
    readBracketCharacter(tokenizer, char);
  }
}

/** A `.`: the current word becomes a property step, unless it directly follows an array step (`A[].B`). */
function readDot(tokenizer: Tokenizer) {
  if (!(tokenizer.currentWord === '' && isLastItemArrayStep(tokenizer.items))) {
    pushStep(tokenizer, { name: tokenizer.currentWord, operator: JSON_PATH_ACTIONS.objectHasProperty });
  }
  tokenizer.index++;
}

/**
 * A `?` that ends a step (`A?.B`, `A??`, or `A?` at the end): the current word
 * becomes a "where at least one" step. Any other `?` is part of the name.
 */
function readQuestionMark(tokenizer: Tokenizer): boolean {
  const nextChar = tokenizer.str[tokenizer.index + 1];
  if (nextChar !== '.' && nextChar !== '?' && nextChar != null) {
    return false;
  }
  pushStep(tokenizer, { name: tokenizer.currentWord, operator: JSON_PATH_ACTIONS.arrayOrObjectHasPropertyOrValue });
  tokenizer.index += nextChar === '.' ? 2 : 1;
  return true;
}

function readOutsideSquareBrackets(tokenizer: Tokenizer, char: string) {
  if (char === '.') {
    readDot(tokenizer);
    return;
  }
  if (char === '?' && readQuestionMark(tokenizer)) {
    return;
  }
  if (char === '[') {
    tokenizer.isInSquareBrackets = true;
    tokenizer.index++;
    return;
  }
  // A backslash escapes the next character, so a bracket or dot after it is
  // literal. The backslash itself stays in the name.
  if (char === '\\') {
    tokenizer.isEscaping = true;
  }
  tokenizer.currentWord += char;
  tokenizer.index++;
}

function readCharacter(tokenizer: Tokenizer) {
  const char = tokenizer.str[tokenizer.index];
  if (tokenizer.isEscaping) {
    tokenizer.currentWord += char;
    tokenizer.isEscaping = false;
    tokenizer.index++;
  } else if (tokenizer.isInSquareBrackets) {
    readInSquareBrackets(tokenizer, char);
  } else {
    readOutsideSquareBrackets(tokenizer, char);
  }
}

/** The last level: the word left over, or the element value itself after a trailing array step. */
function finishTokenizer(tokenizer: Tokenizer): JsonPathItem[] {
  const endsOnArrayStep = isLastItemArrayStep(tokenizer.items);
  if (tokenizer.currentWord !== '') {
    const item: JsonPathItem = { name: tokenizer.currentWord, operator: '' };
    if (endsOnArrayStep) {
      item.arrayConditionType = 'property';
    }
    tokenizer.items.push(item);
  } else if (endsOnArrayStep) {
    tokenizer.items.push({ name: '', operator: '', arrayConditionType: 'value' });
  }
  return tokenizer.items;
}

/**
 * Splits a flattened path into its levels, escaping and quoted bracket keys
 * included. A string with no path syntax is a single level.
 */
export function parseJsonAttributePath(str: string): JsonPathItem[] {
  const tokenizer: Tokenizer = {
    str,
    index: 0,
    items: [],
    currentWord: '',
    isEscaping: false,
    isInSquareBrackets: false,
    maybeBracketsWord: false,
    bracketsWord: null,
    arrayIndexString: null,
  };
  while (tokenizer.index < str.length) {
    readCharacter(tokenizer);
  }
  return finishTokenizer(tokenizer);
}

/** A key that is numeric, or holds path syntax, is written as `["key"]`. */
const RE_KEY_NEEDS_ESCAPING = /[\s.()[\]{}/@#$%^&*+=<>?!:;'"`,~|\\]/;

function isNumericKey(value: string): boolean {
  return Number.isFinite(Number.parseFloat(value));
}

function accessorFor(pathString: string, accessorType: 'array' | 'object'): string {
  if (accessorType === 'array') {
    return '?.';
  }
  return pathString.endsWith('.') ? '' : '.';
}

/**
 * One level appended to the path. An array step reads `?.`, an object step
 * reads `.`, and a key needing escape is bracket-quoted after the array
 * step's `?.`, or in place of the object step's `.`. A path cannot
 * start with an accessor, so a leading `.` or `?.` is trimmed.
 */
function appendPathSegment(pathString: string, name: string, accessorType: 'array' | 'object', escape: boolean) {
  if (escape && (isNumericKey(name) || RE_KEY_NEEDS_ESCAPING.test(name))) {
    // Below an array step the quoted key still needs its `?.`: the path reader
    // takes `A["key"]` as A's own property, and `A?.["key"]` as each element's.
    return `${pathString}${accessorType === 'array' ? '?.' : ''}["${name}"]`;
  }
  let appended = `${pathString}${accessorFor(pathString, accessorType)}${name}`;
  if (appended.startsWith('.')) {
    appended = appended.substring(1);
  }
  return appended.startsWith('?.') ? appended.substring(2) : appended;
}

/**
 * Flattens levels back into the path string. The root level is never
 * bracket-escaped, because it is the attribute's own name rather than a key
 * inside its JSON.
 */
export function formatJsonPathItems(items: readonly JsonPathItem[]): string {
  let pathString = '';
  items.forEach((item, index) => {
    pathString = appendPathSegment(
      pathString,
      item.name,
      item.arrayConditionType != null ? 'array' : 'object',
      index !== 0,
    );
  });
  // A trailing `?.` keeps only the question mark: `A?` matches the array
  // element's value itself.
  return pathString.endsWith('?.') ? pathString.slice(0, -1) : pathString;
}

/**
 * Whether the field holds a path at all: at least two levels, and not the
 * unsupported `[].A` head.
 */
export function hasJsonPath(field: string): boolean {
  const items = parseJsonAttributePath(field);
  if (items.length <= 1) {
    return false;
  }
  return !(items[0].name === '' && items[0].operator === JSON_PATH_ACTIONS.arrayHasPropertyOrValue);
}

/**
 * Whether the level-by-level builder can hold this path. `[]` and `[n]` steps
 * can only be edited as free text, so a path carrying them keeps its stored
 * bytes until the user converts it.
 */
export function isBasicEditablePath(items: readonly JsonPathItem[]): boolean {
  return items.every(
    (item) =>
      item.operator !== JSON_PATH_ACTIONS.arrayHasPropertyOrValue &&
      item.operator !== JSON_PATH_ACTIONS.arrayIndexHasPropertyOrValue,
  );
}

/**
 * Whether a free-text path may switch to the level-by-level builder: no deeper
 * than the builder allows, and no array index. A `[]` step may switch, once the
 * user confirms it will be rewritten as `?`.
 */
export function canConvertToBasic(items: readonly JsonPathItem[]): boolean {
  return (
    items.length > 0 &&
    items.length <= MAX_JSON_DEPTH + 1 &&
    items.every((item) => item.operator !== JSON_PATH_ACTIONS.arrayIndexHasPropertyOrValue)
  );
}

/** Whether the level at this index may still add a step below it. */
export function canAddJsonPathLevel(levelIndex: number): boolean {
  return levelIndex < MAX_JSON_DEPTH;
}

/**
 * The attribute a path starts from, for a lookup keyed on the attribute rather
 * than the whole path (value suggestions). A single-level field is its own
 * root, however bracket-flavored its name, and so is the `[].A` head shape,
 * whose first level has no name.
 */
export function jsonPathRootField(field: string): string {
  const steps = parseJsonAttributePath(field);
  const root = steps[0]?.name ?? '';
  if (steps.length <= 1 || root === '') {
    return field;
  }
  return root;
}

/**
 * Whether the flattened string alone reconstructs these levels. It does not
 * for a half-built level: `A.` loses its empty child, and an unnamed property
 * level after `?.` reads back as a value level.
 */
export function jsonPathItemsRoundTrip(items: readonly JsonPathItem[]): boolean {
  const reparsed = parseJsonAttributePath(formatJsonPathItems(items));
  return (
    reparsed.length === items.length &&
    reparsed.every(
      (item, index) =>
        item.name === items[index].name &&
        item.operator === items[index].operator &&
        item.arrayConditionType === items[index].arrayConditionType &&
        item.arrayIndex === items[index].arrayIndex,
    )
  );
}

/**
 * One edit to a JSON path, as the builder reports it to a surface that
 * records them: a level added or removed, or the path or its comparison
 * edited, in the level-by-level (`'basic'`) or free-text (`'advanced'`)
 * layout.
 */
export type JsonPathEdit = {
  interface: 'basic' | 'advanced';
  editType: 'added' | 'deleted' | 'edited';
};
